import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Plus, Search, Filter, Zap, Users, Clock, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSocket } from '../hooks/useSocket'
import { useAuthStore } from '../hooks/useAuthStore'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import GameSetupModal from '../components/GameSetupModal'
import GameCard from '../components/GameCard'
import PlayerList from '../components/PlayerList'

const LobbyPage = () => {
  const { t } = useTranslation('lobby')
  const { t: tCommon } = useTranslation('common')
  const { user } = useAuthStore()
  const { games, onlineUsers, quickJoin, currentGame, availableGameTypes } = useSocket()
  useDocumentTitle(t('title'))

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [gameTypeFilter, setGameTypeFilter] = useState('all')
  const [sortBy, setSortBy] = useState('recent')

  const filteredGames = games
    .filter(game => {
      if (searchTerm && !game.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false
      }
      if (gameTypeFilter !== 'all' && game.type !== gameTypeFilter) {
        return false
      }
      return true
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'players':
          return (b.players?.length || 0) - (a.players?.length || 0)
        case 'name':
          return a.name.localeCompare(b.name)
        default:
          // 'recent': newest games first
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      }
    })

  if (currentGame) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-effect rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4">{t('currentGame')}</h2>
            <GameCard game={currentGame} isCurrentGame={true} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('title')}</h1>
          <p className="text-gray-400">{t('subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Quick Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCreateModal(true)}
                className="glass-effect rounded-xl p-6 text-left hover:bg-blue-600/20 transition-colors group"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-600 rounded-lg group-hover:bg-blue-500 transition-colors">
                    <Plus className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{t('createGame.title')}</h3>
                    <p className="text-gray-400">{t('createGame.subtitle')}</p>
                  </div>
                </div>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => quickJoin()}
                className="glass-effect rounded-xl p-6 text-left hover:bg-green-600/20 transition-colors group"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-600 rounded-lg group-hover:bg-green-500 transition-colors">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{t('quickJoin.title')}</h3>
                    <p className="text-gray-400">{t('quickJoin.subtitle')}</p>
                  </div>
                </div>
              </motion.button>
            </div>

            {/* Filters and Search */}
            <div className="glass-effect rounded-xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t('searchPlaceholder')}
                      className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <select
                  value={gameTypeFilter}
                  onChange={(e) => setGameTypeFilter(e.target.value)}
                  className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">{tCommon('gameType.all')}</option>
                  {availableGameTypes.map((gameType) => (
                    <option key={gameType.name} value={gameType.name}>
                      {gameType.displayName}
                    </option>
                  ))}
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="recent">{tCommon('sort.recent')}</option>
                  <option value="players">{tCommon('sort.players')}</option>
                  <option value="name">{tCommon('sort.name')}</option>
                </select>
              </div>
            </div>

            {/* Games Grid */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">{t('availableGames')}</h2>
                <span className="text-gray-400">{t('gamesFound', { count: filteredGames.length })}</span>
              </div>

              {filteredGames.length === 0 ? (
                <div className="glass-effect rounded-xl p-12 text-center">
                  <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{t('noGamesFound.title')}</h3>
                  <p className="text-gray-400 mb-4">
                    {searchTerm || gameTypeFilter !== 'all'
                      ? t('noGamesFound.messageFiltered')
                      : t('noGamesFound.messageEmpty')}
                  </p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    {t('createGame.title')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredGames.map((game) => (
                    <GameCard key={game.id} game={game} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <PlayerList players={onlineUsers} />
            
            {/* Game Stats */}
            <div className="glass-effect rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">{t('statistics.title')}</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">{t('statistics.onlinePlayers')}</span>
                  <span className="text-white font-medium">{onlineUsers.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">{t('statistics.activeGames')}</span>
                  <span className="text-white font-medium">{games.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">{t('statistics.availableSlots')}</span>
                  <span className="text-white font-medium">
                    {games.reduce((acc, game) => acc + (game.maxPlayers - game.players.length), 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Game Modal */}
      {showCreateModal && (
        <GameSetupModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  )
}

export default LobbyPage